// TerminPeekPopover.tsx — Einzelklick-Peek eines Terminblocks (Termine-SP4,
// A16): vollständig lesbarer Betreff, Zeitraum, Organisator (Screenshot-Gap),
// Ort, Serien-Indikator, Teams-Kamera + „Jetzt teilnehmen", Art-Badge
// (A13: Anzeige bleibt) und die Aktionen „Öffnen/Bearbeiten" + „Löschen"
// (nur WRITE). Doppelklick öffnet weiterhin direkt das Termin-Fenster
// (Verdrahtung in CalendarView). SP8 (A17): zusätzlich „Neuer Vorgang"
// (Kontakt-Ableitung aus Teilnehmer-E-Mails) und „Zu Vorgang hinzufügen"
// (bestehenden Vorgang binden) — beide via TerminVorgangDialog, nur WRITE.
//
// Desktop: Portal an `document.body` mit `position:fixed` am Block-Anker
// (RdPopover-Pattern — nie vom RBC-Scroll-Container abgeschnitten), Klemmung
// über clampPopoverPosition (peek.ts). Mobil (Phone ≤768, Entscheidung 69):
// BottomSheet statt Popover. Der Portal-Root trägt `.cal-rd`, damit die
// CSS-Custom-Properties aus calendar.css greifen (Klasse setzt nur Variablen —
// keine Portal-Falle wie bei .kvp-rd min-height).

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { useNavigate } from "react-router";
import {
    Add16Regular,
    ArrowRepeatAll16Regular,
    Delete16Regular,
    Dismiss16Regular,
    LinkSquare16Regular,
    LockClosed16Regular,
    Notepad16Regular,
    Open16Regular,
    Video16Regular,
} from "@fluentui/react-icons";
import { useResponsive } from "../../hooks/useResponsive";
import { calendarService } from "../../services/calendarService";
import { istVertraulich } from "./terminPrivatAnzeige";
import { calendarTerminService } from "../../services/calendarTerminService";
import { requestCalendarReload } from "./CalendarStateContext";
import BottomSheet from "./BottomSheet";
import AttendeeChips from "./AttendeeChips";
import TerminFarbPicker from "./TerminFarbPicker";
import { classifyTermin, isSeriesEvent } from "./types";
import type { CalendarGridEvent } from "./types";
import { farbeAusMeta, hellerHintergrund, KALENDER_AKZENT } from "./terminBlock";
import { ART_LABEL, clampPopoverPosition, zeitraumLabel } from "./peek";
import type { PeekAnchor } from "./peek";
import {
    NeuerVorgangAusTerminDialog,
    VorgangZuweisenDialog,
    type TerminVorgangKontext,
} from "./TerminVorgangDialog";
import { bestaetigen, hinweisen } from "../redesign/bestaetigen";
import { alleFenster, aktivesFenster } from "../windowManager/browserFenster/fensterRegister";

/** Ein offener Peek: Event + Anker (null ⇒ Viewport-Mitte bzw. BottomSheet). */
export interface TerminPeek {
    event: CalendarGridEvent;
    anchor: PeekAnchor | null;
}

/**
 * Anker aus dem RBC-Klick-Event ableiten: rechte Oberkante des angeklickten
 * `.rbc-event`-Blocks (Outlook-Peek-Position); Fallback = Mausposition.
 */
export function peekAnchorFromDom(e?: React.SyntheticEvent): PeekAnchor | null {
    const target = e?.target as HTMLElement | undefined;
    const block = target?.closest?.(".rbc-event");
    if (block) {
        const r = block.getBoundingClientRect();
        return { x: r.right, y: r.top };
    }
    const me = e as unknown as React.MouseEvent | undefined;
    if (me && typeof me.clientX === "number" && typeof me.clientY === "number") {
        return { x: me.clientX, y: me.clientY };
    }
    return null;
}

export interface TerminPeekPopoverProps {
    peek: TerminPeek | null;
    onClose: () => void;
    /** WRITE-Gating (Löschen) — doppelt durch assertCalendarWrite im Backend. */
    canEditEvent: (e: CalendarGridEvent) => boolean;
    /** „Öffnen/Bearbeiten" → volles Termin-Fenster (read-only bei fremden Kalendern). */
    onOpenEdit: (e: CalendarGridEvent) => void;
}

/** Lese-Inhalt des Peeks (Desktop-Popover UND Mobile-BottomSheet teilen ihn). */
const PeekInhalt: React.FC<{
    event: CalendarGridEvent;
    onClose?: () => void;
    /** K9: Absprung auf den verknüpften Vorgang. */
    onVorgangOeffnen?: (vorgangId: number) => void;
}> = ({ event, onClose, onVorgangOeffnen }) => {
    const cls = classifyTermin(event.meta);
    const akzent = farbeAusMeta(event.meta) || event.source?.color || KALENDER_AKZENT;
    const serie = isSeriesEvent(event);
    // SP5 (A8): kollabierte Chip-Liste statt bloßer Anzahl — geteilte
    // Komponente mit dem Termin-Fenster; Räume (resource) zählen nicht.
    const teilnehmerChips = (event.attendees ?? [])
        .filter((a) => a.type !== "resource")
        .map((a) => ({ address: a.address, name: a.name?.trim() || a.address }));
    const ghosts = event.ghostSources ?? [];
    const preview = (event.bodyPreview ?? "").trim();
    return (
        <div className="cal-peek-inhalt">
            <div className="cal-peek-head">
                <span
                    className="cal-peek-badge"
                    style={{
                        background: hellerHintergrund(akzent, 0.16),
                        borderLeft: `3px solid ${akzent}`,
                    }}
                >
                    {ART_LABEL[cls]}
                </span>
                {onClose && (
                    <button
                        type="button"
                        className="cal-peek-x"
                        aria-label="Schließen"
                        onClick={onClose}
                    >
                        <Dismiss16Regular />
                    </button>
                )}
            </div>
            {/* Voller Betreff, mehrzeilig — im Grid ist er ge-ellipst (A16). */}
            <h3 className="cal-peek-titel">{event.title || "(ohne Titel)"}</h3>
            {/* K9: der Vorgang am Termin — klickbar, wenn er lesbar ist. */}
            {event.meta?.vorgangId != null && (
                <div className="cal-peek-zeile">
                    <span className="cal-peek-lbl">Vorgang</span>
                    <button
                        type="button"
                        className="cal-peek-linkval"
                        onClick={() => onVorgangOeffnen?.(event.meta?.vorgangId as number)}
                    >
                        {event.meta?.vorgangTitel?.trim() || `#${event.meta?.vorgangId}`}
                    </button>
                </div>
            )}
            <div className="cal-peek-zeile">
                {zeitraumLabel(event.start, event.end, event.allDay)}
            </div>
            {/* PO 28.09.2026: Termin aus der Ebene „Termine meiner Kontakte". */}
            {event.ueberKontakt && (
                <div className="cal-peek-zeile">
                    <span className="cal-peek-lbl">Kontakt</span>
                    <span>
                        {event.ueberKontakt.name}
                        {" — "}
                        {event.ueberKontakt.weg === "zuweisung" ? "zugewiesen" : "im Verteiler"}
                        {", aus einem fremden Kalender (nur ansehen, Notizen möglich)"}
                    </span>
                </div>
            )}
            {serie && (
                <div className="cal-peek-zeile cal-peek-serie">
                    <ArrowRepeatAll16Regular aria-hidden /> Serientermin
                </div>
            )}
            {/* Privater Termin (PO 27.07.2026). Am EIGENEN Termin die Bestätigung,
                dass die Markierung sitzt — ohne den Hinweis bliebe unklar, ob das
                Häkchen gewirkt hat. An einem fremden erklärt er, warum hier nur
                „Privat" steht. */}
            {istVertraulich(event.sensitivity) && (
                <div className="cal-peek-zeile cal-peek-privat">
                    <LockClosed16Regular aria-hidden /> Privat — für andere nicht einsehbar
                </div>
            )}
            {event.organizer && (
                <div className="cal-peek-zeile">
                    <span className="cal-peek-lbl">Organisator</span>
                    <span>{event.organizer.name || event.organizer.address}</span>
                </div>
            )}
            {event.location && (
                <div className="cal-peek-zeile">
                    <span className="cal-peek-lbl">Ort</span>
                    <span>{event.location}</span>
                </div>
            )}
            {teilnehmerChips.length > 0 && (
                <div className="cal-peek-zeile" style={{ alignItems: "flex-start" }}>
                    <span className="cal-peek-lbl">Teilnehmer</span>
                    <AttendeeChips attendees={teilnehmerChips} />
                </div>
            )}
            {event.onlineMeetingUrl && (
                <a
                    className="cal-peek-join"
                    href={event.onlineMeetingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <Video16Regular aria-hidden /> Jetzt teilnehmen
                </a>
            )}
            {ghosts.length > 0 && (
                <div className="cal-peek-ghosts">
                    auch in: {ghosts.map((g) => g.displayName || g.address).join(", ")}
                </div>
            )}
            {preview && <div className="cal-peek-preview">{preview}</div>}
        </div>
    );
};

export const TerminPeekPopover: React.FC<TerminPeekPopoverProps> = ({
    peek,
    onClose,
    canEditEvent,
    onOpenEdit,
}) => {
    const { isPhone } = useResponsive();
    // Bewusst die GLOBALE Reload-Anforderung statt `useCalendarState()`: der
    // Peek steht seit 14.09.2026 auch außerhalb des Kalenders (Termin-Notizen),
    // wo es keinen CalendarStateProvider gibt — der Hook würde dort werfen.
    // Wirkung ist dieselbe: das Raster hört auf genau dieses Signal.
    const requestReload = requestCalendarReload;
    const navigate = useNavigate();
    const popRef = useRef<HTMLDivElement>(null);
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
    const [deleting, setDeleting] = useState(false);
    // SP6/A10: individuelle Termin-Farbe — lokaler Stand für sofortiges
    // Feedback (Badge/Picker), der Grid-Stand kommt über requestReload nach.
    const [farbe, setFarbe] = useState<string | null>(null);

    // Beim Öffnen/Wechsel des Peeks die Farbe aus dem Event-Meta übernehmen.
    useEffect(() => {
        setFarbe(peek?.event.meta?.farbe ?? null);
    }, [peek]);

    // Desktop: nach dem Render die echte Größe messen und am Anker in den
    // Viewport klemmen (bis dahin unsichtbar — kein Sprung-Flackern).
    useLayoutEffect(() => {
        if (!peek || isPhone) {
            setPos(null);
            return;
        }
        const el = popRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const anchor: PeekAnchor = peek.anchor ?? {
            x: Math.round(aktivesFenster().innerWidth / 2 - r.width / 2),
            y: Math.round(aktivesFenster().innerHeight / 2 - r.height / 2),
        };
        setPos(
            clampPopoverPosition(
                anchor,
                { width: r.width, height: r.height },
                { width: aktivesFenster().innerWidth, height: aktivesFenster().innerHeight },
            ),
        );
    }, [peek, isPhone]);

    // Außenklick/Escape/Scroll/Resize schließen (Desktop-Popover). Der öffnende
    // Klick ist schon vorbei (mousedown → click) — schließt nicht sofort wieder.
    useEffect(() => {
        if (!peek || isPhone) return;
        const onDown = (e: MouseEvent) => {
            if (popRef.current?.contains(e.target as Node)) return;
            onClose();
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        const onMove = () => onClose();
        alleFenster.addEventListener("pointerdown", onDown);
        alleFenster.addEventListener("keydown", onKey);
        alleFenster.addEventListener("resize", onMove);
        alleFenster.addEventListener("scroll", onMove, true); // capture: RBC-Scroller
        return () => {
            alleFenster.removeEventListener("pointerdown", onDown);
            alleFenster.removeEventListener("keydown", onKey);
            alleFenster.removeEventListener("resize", onMove);
            alleFenster.removeEventListener("scroll", onMove, true);
        };
    }, [peek, isPhone, onClose]);

    // Löschen (nur WRITE): gleiche Confirm-/Fehlerführung wie TerminFormWindow.
    const handleDelete = useCallback(async () => {
        if (!peek) return;
        const ev = peek.event;
        if (
            !(await bestaetigen({
                titel: "Termin löschen?",
                text: "Der Termin wird auch im Outlook-Kalender gelöscht.",
                ja: "Löschen",
                ton: "danger",
            }))
        )
            return;
        setDeleting(true);
        try {
            await calendarService.deleteEvent(ev.mailbox, ev.id);
            requestReload();
            onClose();
        } catch (err) {
            console.error("[TerminPeekPopover] Löschen fehlgeschlagen", err);
            void hinweisen({ titel: "Termin nicht gelöscht", text: "Der Termin konnte nicht gelöscht werden. Bitte versuche es noch einmal.", ton: "danger" });
        } finally {
            setDeleting(false);
        }
    }, [peek, requestReload, onClose]);

    // Farbe setzen/entfernen (SP6/A10): sofort speichern (der Peek hat keinen
    // Save-Button), optimistisch anzeigen, Grid über requestReload nachziehen.
    // Backend prüft das Schreibrecht doppelt (assertCalendarWrite ⇒ 403/404).
    const handleFarbe = useCallback(
        async (hex: string | null) => {
            if (!peek) return;
            const ev = peek.event;
            const vorher = farbe;
            setFarbe(hex);
            try {
                await calendarTerminService.setTerminFarbe(ev.mailbox, ev.id, hex);
                requestReload();
            } catch (err) {
                console.error("[TerminPeekPopover] Farbe setzen fehlgeschlagen", err);
                setFarbe(vorher);
            }
        },
        [peek, farbe, requestReload],
    );

    // Notiz-Seite öffnen (SP7/A12): die Route trägt die Sidecar-terminId —
    // per getMeta auflösen; existiert noch kein Sidecar, legt ensureMeta ihn an
    // (nur mit WRITE — read-only ohne Sidecar hat schlicht noch keine Notizen).
    const handleNotizen = useCallback(async () => {
        if (!peek) return;
        const ev = peek.event;
        try {
            let meta = await calendarTerminService.getMeta(ev.mailbox, ev.id);
            // Kontakt-Zugang (PO 28.09.2026): FO/BO dürfen die Notiz-Seite
            // anlegen — das Backend erlaubt die Sidecar-Anlage auf diesem Weg.
            if (meta.terminId == null && (canEditEvent(ev) || !!ev.ueberKontakt)) {
                meta = await calendarTerminService.ensureMeta(ev.mailbox, ev.id);
            }
            if (meta.terminId == null) {
                void hinweisen({ titel: "Noch keine Notizen", text: "Für diesen Termin gibt es noch keine Notizen." });
                return;
            }
            onClose();
            navigate(`/termine/notizen/${meta.terminId}`);
        } catch (err) {
            console.error("[TerminPeekPopover] Notiz-Seite öffnen fehlgeschlagen", err);
            void hinweisen({ titel: "Notizen nicht geöffnet", text: "Die Notizen konnten nicht geöffnet werden. Bitte versuche es noch einmal.", ton: "danger" });
        }
    }, [peek, canEditEvent, navigate, onClose]);

    /**
     * K2 — die Aktionen „Vorgang verknüpfen" und „Neuer Vorgang aus Termin"
     * sind BEWUSST NICHT write-gegated (E10): das Verknüpfen schreibt nur
     * KMVS-Metadaten am Sidecar, im Graph-Kalender ändert sich nichts. Sie
     * stehen deshalb auch auf CBB- und reinen Lesekalendern zur Verfügung.
     */
    const [zuweisenKontext, setZuweisenKontext] = useState<{
        mailbox: string;
        eventId: string;
        contactId?: number | null;
    } | null>(null);
    const [neuerVorgangKontext, setNeuerVorgangKontext] = useState<TerminVorgangKontext | null>(
        null,
    );

    const oeffneVorgang = useCallback(
        (vorgangId: number) => {
            onClose();
            navigate(`/vorgaenge/${vorgangId}`);
        },
        [navigate, onClose],
    );

    // Die Dialoge liegen AUSSERHALB des `if (!peek) return null` — sonst
    // verschwänden sie in dem Moment, in dem der Peek sich schließt (und das
    // tut er beim Öffnen des Dialogs).
    const dialoge = (
        <>
            <VorgangZuweisenDialog
                kontext={zuweisenKontext}
                onClose={() => setZuweisenKontext(null)}
                onGebunden={() => {
                    setZuweisenKontext(null);
                    requestReload();
                }}
            />
            <NeuerVorgangAusTerminDialog
                kontext={neuerVorgangKontext}
                onClose={() => setNeuerVorgangKontext(null)}
                onGebunden={() => {
                    setNeuerVorgangKontext(null);
                    requestReload();
                }}
            />
        </>
    );

    if (!peek) return dialoge;
    const ev = peek.event;
    const kannBearbeiten = canEditEvent(ev);
    // Lokale Farbe ins Meta mergen → Badge/Akzent im Peek reagieren sofort
    // (das Grid-Event selbst wird erst durch den Reload aktualisiert).
    const evMitFarbe: CalendarGridEvent = {
        ...ev,
        meta: { terminArt: null, kundeId: null, ...(ev.meta ?? {}), farbe },
    };

    // Farbpunkt-Reihe (nur WRITE — FE blendet aus, Backend sichert final ab).
    const farbReihe = kannBearbeiten ? (
        <div className="cal-peek-zeile" style={{ alignItems: "center", padding: "0 16px 10px" }}>
            <span className="cal-peek-lbl">Farbe</span>
            <TerminFarbPicker value={farbe} onSelect={(hex) => void handleFarbe(hex)} />
        </div>
    ) : null;

    /** Ziel der beiden Vorgangs-Aktionen. */
    const vorgangKontext: TerminVorgangKontext = {
        mailbox: ev.mailbox,
        eventId: ev.id,
        attendees: ev.attendees ?? [],
        organizer: ev.organizer ?? null,
    };

    const vorgangVerknuepfenBtn = (
        <button
            type="button"
            className="cal-peek-btn"
            onClick={() => {
                // K10: der Kunde des Termins steuert im Picker die Rangstufe 1.
                setZuweisenKontext({
                    mailbox: ev.mailbox,
                    eventId: ev.id,
                    contactId: ev.meta?.kundeId ?? null,
                });
                onClose();
            }}
        >
            <LinkSquare16Regular aria-hidden />{" "}
            {ev.meta?.vorgangId != null ? "Vorgang ändern…" : "Vorgang verknüpfen…"}
        </button>
    );

    const neuerVorgangBtn = (
        <button
            type="button"
            className="cal-peek-btn"
            onClick={() => {
                setNeuerVorgangKontext(vorgangKontext);
                onClose();
            }}
        >
            <Add16Regular aria-hidden /> Neuer Vorgang aus Termin…
        </button>
    );

    /**
     * `erweitert` unterscheidet Karte und Liste. Auf dem DESKTOP ist der Peek
     * eine Karte mit begrenzter Höhe — PR #872 hat sie aus genau diesem Grund
     * entschlackt, deshalb kommt dort GENAU EINE Zeile dazu; der zweite
     * Einstieg liegt im Rechtsklick-Menü. Auf dem TELEFON gibt es kein
     * Kontextmenü, und das BottomSheet ist eine scrollbare Liste — dort stehen
     * BEIDE Aktionen, sonst wäre K2 auf dem Telefon nur halb erfüllt.
     */
    const aktionen = (erweitert: boolean) => (
        <>
            <button
                type="button"
                className="cal-peek-btn cal-peek-btn-primary"
                onClick={() => {
                    onOpenEdit(ev);
                    onClose();
                }}
            >
                <Open16Regular aria-hidden /> {kannBearbeiten ? "Öffnen / Bearbeiten" : "Öffnen"}
            </button>
            {/* SP7/A12: Notiz-Seite im OneNote-Stil (auch read-only lesbar). */}
            <button
                type="button"
                className="cal-peek-btn"
                onClick={() => void handleNotizen()}
            >
                <Notepad16Regular aria-hidden /> Notizen
            </button>
            {/* Kontakt-Ebene: Verknüpfen braucht Kalender-Leserecht (E10). */}
            {!ev.ueberKontakt && vorgangVerknuepfenBtn}
            {erweitert && !ev.ueberKontakt && neuerVorgangBtn}
            {kannBearbeiten && (
                <button
                    type="button"
                    className="cal-peek-btn cal-peek-btn-danger"
                    onClick={() => void handleDelete()}
                    disabled={deleting}
                >
                    <Delete16Regular aria-hidden /> {deleting ? "Lösche…" : "Löschen"}
                </button>
            )}
        </>
    );

    // Mobil (Phone ≤768, Entscheidung 69): BottomSheet statt Popover.
    if (isPhone) {
        return (
            <>
            <BottomSheet
                open
                title="Termin"
                onClose={onClose}
                footer={
                    <div className="cal-peek-actions cal-peek-actions-sheet">{aktionen(true)}</div>
                }
            >
                <PeekInhalt event={evMitFarbe} onVorgangOeffnen={oeffneVorgang} />
                {farbReihe}
            </BottomSheet>
            {dialoge}
            </>
        );
    }

    return createPortal(
        <>
            {/* Weißer Backdrop: verdeckt/verwischt den Kalender hinter dem Peek,
                sodass nur die Popup-Infos sichtbar sind (Review-Wunsch).
                Klick schließt (wie der bestehende Außenklick-Handler). */}
            <div
                className="cal-peek-backdrop"
                onPointerDown={onClose}
                style={{ visibility: pos ? "visible" : "hidden" }}
            />
            <div
                ref={popRef}
                className="cal-rd cal-peek"
                role="dialog"
                aria-label={ev.title || "Termin"}
                style={{
                    position: "fixed",
                    top: pos?.top ?? -9999,
                    left: pos?.left ?? -9999,
                    visibility: pos ? "visible" : "hidden",
                }}
            >
                <PeekInhalt
                    event={evMitFarbe}
                    onClose={onClose}
                    onVorgangOeffnen={oeffneVorgang}
                />
                {farbReihe}
                <div className="cal-peek-actions">{aktionen(false)}</div>
            </div>
            {dialoge}
        </>,
        document.body,
    );
};

export default TerminPeekPopover;
